import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { LEGAL_COMPANY_NAME, LEGAL_HOSTING_PROVIDER, PUBLIC_LEGAL_URLS } from '../../core/legal/public-legal.constants';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './login.component.html'
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  readonly privacyPolicyUrl = PUBLIC_LEGAL_URLS.privacyPolicy;
  readonly termsOfServiceUrl = PUBLIC_LEGAL_URLS.termsOfService;
  readonly legalCompanyName = LEGAL_COMPANY_NAME;
  readonly legalHostingProvider = LEGAL_HOSTING_PROVIDER;

  readonly loading = signal(false);
  readonly feedback = signal<string | null>(null);
  readonly feedbackType = signal<'info' | 'error'>('info');
  readonly resetExpanded = signal(false);
  readonly resetLoading = signal(false);
  readonly resetFeedback = signal<string | null>(null);
  readonly resetFeedbackType = signal<'info' | 'error' | 'success'>('info');

  readonly form = this.fb.nonNullable.group({
    identifier: ['', Validators.required],
    password: ['', Validators.required]
  });

  readonly resetForm = this.fb.nonNullable.group({
    currentPassword: ['', Validators.required],
    newPassword: ['', [Validators.required, Validators.minLength(8)]],
    confirmNewPassword: ['', Validators.required]
  });

  submit(): void {
    if (this.form.invalid || this.loading()) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.feedbackType.set('info');
    this.feedback.set('Connexion en cours...');

    const { identifier, password } = this.form.getRawValue();

    this.authService.login(identifier.trim(), password).subscribe({
      next: (response) => {
        this.feedback.set('Connexion reussie.');
        this.router.navigateByUrl(this.authService.getDefaultAppRoute(response.user));
      },
      error: (error) => {
        this.loading.set(false);
        this.feedbackType.set('error');
        this.feedback.set(
          error?.error?.error === 'INVALID_CREDENTIALS'
            ? 'Identifiant ou mot de passe invalide.'
            : error?.error?.error === 'ACCOUNT_INVITATION_PENDING'
              ? 'Activez votre compte depuis l email d invitation.'
              : error?.error?.error === 'ACCOUNT_DISABLED'
                ? 'Ce compte n est plus accessible.'
            : error?.error?.error === 'COMPANY_NOT_FOUND'
              ? 'Ce compte n est rattache a aucune compagnie active.'
            : 'Impossible de vous connecter.'
        );
      },
      complete: () => this.loading.set(false)
    });
  }

  toggleResetPassword(): void {
    this.resetExpanded.update((value) => !value);
    this.resetFeedback.set(null);
    if (!this.resetExpanded()) {
      this.resetForm.reset();
    }
  }

  submitPasswordReset(): void {
    if (this.resetLoading()) {
      return;
    }

    const identifier = this.form.controls.identifier.getRawValue().trim();
    if (!identifier) {
      this.form.controls.identifier.markAsTouched();
      this.resetFeedbackType.set('error');
      this.resetFeedback.set('Renseignez d abord votre email ou identifiant.');
      return;
    }

    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      return;
    }

    const { currentPassword, newPassword, confirmNewPassword } = this.resetForm.getRawValue();
    if (newPassword !== confirmNewPassword) {
      this.resetFeedbackType.set('error');
      this.resetFeedback.set('La confirmation du nouveau mot de passe ne correspond pas.');
      return;
    }

    this.resetLoading.set(true);
    this.resetFeedbackType.set('info');
    this.resetFeedback.set('Mise a jour du mot de passe...');

    this.authService.changePassword(identifier, currentPassword, newPassword).subscribe({
      next: () => {
        this.resetFeedbackType.set('success');
        this.resetFeedback.set('Mot de passe mis a jour. Vous pouvez maintenant vous connecter.');
        this.resetForm.reset();
      },
      error: (error) => {
        this.resetFeedbackType.set('error');
        this.resetFeedback.set(
          error?.error?.error === 'INVALID_CREDENTIALS'
            ? 'Le mot de passe actuel est invalide.'
            : error?.error?.error === 'PASSWORD_TOO_SHORT'
              ? 'Le nouveau mot de passe doit contenir au moins 8 caracteres.'
              : error?.error?.error === 'PASSWORD_UNCHANGED'
                ? 'Choisissez un nouveau mot de passe different.'
                : error?.error?.error === 'ACCOUNT_INVITATION_PENDING'
                  ? 'Activez d abord votre compte depuis l email d invitation.'
                  : error?.error?.error === 'ACCOUNT_DISABLED'
                    ? 'Ce compte n est plus accessible.'
                    : 'Impossible de mettre a jour le mot de passe.'
        );
      },
      complete: () => this.resetLoading.set(false)
    });
  }
}
